import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  SimpleGrid,
  Paper,
  Text,
  Table,
  Tabs,
} from "@mantine/core";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useCellRender } from "../../hooks/useCellRender";
import QuizStatusBadge from "../../components/quiz/QuizStatusBadge";

const apiEntity = "quiz";

const QuizResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tabNav = useTabNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [quiz, setQuiz] = useState(null);
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    const load = async () => {
      const [quiz, statsRes, resultsRes] = await Promise.all([
        api.getById({ apiEntity, id }),
        api.Quiz.getStatistics(id),
        api.Quiz.getResults(id),
      ]);
      setQuiz(quiz);
      setStats(statsRes?.data ?? {});
      setRows(resultsRes?.data ?? []);
    };
    load();
  }, [id]);

  const filtered = useMemo(() => {
    if (filter === "ALL") return rows;
    if (filter === "PENDING") {
      return rows.filter((r) => !r.result || r.status === "NOT_STARTED");
    }
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(
    () => ({
      ALL: rows.length,
      PASSED: rows.filter((r) => r.status === "PASSED").length,
      FAILED: rows.filter((r) => r.status === "FAILED").length,
      PENDING: rows.filter((r) => !r.result || r.status === "NOT_STARTED").length,
    }),
    [rows]
  );

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate(`${basePath}/${id}`);
  };

  return (
    <Page
      title="Quiz Results"
      actions={
        <Group>
          <Button
            onClick={() =>
              api.Quiz.exportResults(id, `quiz-results-${quiz?.code ?? id}.xlsx`)
            }
          >
            Export Results
          </Button>
          <Button variant="default" onClick={handleClose}>
            Close
          </Button>
        </Group>
      }
    >
      {quiz && (
        <Text mb="md" color="dimmed">
          {quiz.title} · {rows.length} participants
        </Text>
      )}

      <SimpleGrid cols={4} mb="lg">
        {[
          { label: "Passed", value: `${stats.passed ?? 0} (${stats.passRate ?? 0}%)` },
          { label: "Failed", value: stats.failed ?? 0 },
          { label: "In Progress", value: stats.inProgress ?? 0 },
          { label: "Average Score", value: `${stats.averageScore ?? 0}%` },
        ].map((card) => (
          <Paper key={card.label} p="md" withBorder>
            <Text size="xs" color="dimmed">
              {card.label}
            </Text>
            <Text size="xl" weight={700}>
              {card.value}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Tabs value={filter} onTabChange={setFilter} mb="md">
        <Tabs.List>
          <Tabs.Tab value="ALL">All ({counts.ALL})</Tabs.Tab>
          <Tabs.Tab value="PASSED">Passed ({counts.PASSED})</Tabs.Tab>
          <Tabs.Tab value="FAILED">Failed ({counts.FAILED})</Tabs.Tab>
          <Tabs.Tab value="PENDING">Pending ({counts.PENDING})</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Table striped highlightOnHover withBorder>
        <thead>
          <tr>
            <th>Participant</th>
            <th>License Type</th>
            <th>Score</th>
            <th>Status</th>
            <th>Try Count</th>
            <th>Completed</th>
            <th>Documents</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => (
            <tr key={row.user?._id}>
              <td>{cellRender.User(row.user)}</td>
              <td>{row.licenceType}</td>
              <td>
                <Text color={row.status === "PASSED" ? "green" : row.status === "FAILED" ? "red" : undefined}>
                  {row.score != null ? `${row.score}%` : "--"}
                </Text>
              </td>
              <td>
                <QuizStatusBadge status={row.status} />
              </td>
              <td>
                {row.attemptCount ?? 0} / {quiz?.maxRetakeCount ?? 100}
              </td>
              <td>
                {row.completedAt
                  ? moment(row.completedAt).format("YYYY-MM-DD HH:mm")
                  : "--"}
              </td>
              <td>{row.documentsAcknowledged}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Page>
  );
};

export default QuizResults;
